import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { HTTPError } from "ky";
import type { QueryClient } from "@tanstack/react-query";
import type {
  BillingInterval,
  CheckoutSessionRequest,
  CheckoutSessionResponse,
  EnterpriseInquiryRequest,
  EnterpriseInquiryResponse,
  IntervalChangeResponse,
  IntervalPreviewResponse,
  PaginatedResponse,
  PlanResponse,
  PortalSessionResponse,
  QuoteRequest,
  QuoteResponse,
  SubscriptionResponse,
  UsageEventResponse,
  UsageResponse,
} from "@/lib/http/billing";
import { api } from "@/lib/http";
import { setOrganizationLocked } from "@/lib/billing-lock";

export const billingQueryKeys = {
  all: ["billing"] as const,
  plans: ["billing", "plans"] as const,
  quote: (request: QuoteRequest) => ["billing", "quote", request] as const,
  subscription: ["billing", "subscription", "me"] as const,
  usage: ["billing", "usage", "me"] as const,
  seatUsage: ["billing", "usage", "seats"] as const,
  usageEvents: (limit: number, offset: number) =>
    ["billing", "usage", "events", { limit, offset }] as const,
  usageCreators: (creatorIds: ReadonlyArray<string>) =>
    ["billing", "usage", "creators", creatorIds] as const,
};

export function invalidateBillingUsage(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: billingQueryKeys.usage });
}

export function invalidateBillingSeatUsage(queryClient: QueryClient) {
  return queryClient.invalidateQueries({
    queryKey: billingQueryKeys.seatUsage,
  });
}

export const plansQueryOptions = queryOptions({
  queryKey: billingQueryKeys.plans,
  queryFn: () =>
    api.payment.raw.get("api/v1/plans").json<Array<PlanResponse>>(),
  staleTime: Infinity,
});

export function quoteQueryOptions(request: QuoteRequest) {
  return queryOptions({
    queryKey: billingQueryKeys.quote(request),
    queryFn: () =>
      api.payment.raw
        .post("api/v1/plans/quote", { json: request })
        .json<QuoteResponse>(),
  });
}

export const subscriptionMeQueryOptions = queryOptions({
  queryKey: billingQueryKeys.subscription,
  queryFn: async () => {
    const subscription = await api.payment.raw
      .get("api/v1/subscriptions/me")
      .json<SubscriptionResponse>();
    setOrganizationLocked(subscription.locked_at !== null);
    return subscription;
  },
});

export function usageMeQueryOptions(refetchInterval: number | false = false) {
  return queryOptions({
    queryKey: billingQueryKeys.usage,
    queryFn: () => api.payment.raw.get("api/v1/usage/me").json<UsageResponse>(),
    staleTime: 60_000,
    refetchInterval,
  });
}

export function seatUsageQueryOptions(refetchInterval: number | false = false) {
  return queryOptions({
    queryKey: billingQueryKeys.seatUsage,
    queryFn: async () => {
      const response = await api.auth.raw
        .get("api/v1/users/", {
          searchParams: { page: 1, per_page: 1 },
        })
        .json<{ count: number }>();
      return response.count;
    },
    staleTime: 60_000,
    refetchInterval,
  });
}

export function usageEventsQueryOptions(limit: number, offset: number) {
  return queryOptions({
    queryKey: billingQueryKeys.usageEvents(limit, offset),
    queryFn: () =>
      api.payment.raw
        .get("api/v1/usage/events", { searchParams: { limit, offset } })
        .json<PaginatedResponse<UsageEventResponse>>(),
  });
}

export function usageCreatorsQueryOptions(creatorIds: ReadonlyArray<string>) {
  return queryOptions({
    queryKey: billingQueryKeys.usageCreators(creatorIds),
    queryFn: async () => {
      const names = new Map<string, string>();
      let currentPage = 1;
      let loadedUsers = 0;
      let totalUsers = 0;

      do {
        const response = await api.auth.raw
          .get("api/v1/users/", {
            searchParams: { page: currentPage, per_page: 100 },
          })
          .json<{
            count: number;
            items: Array<{ id: string; full_name: string }>;
          }>();
        totalUsers = response.count;
        loadedUsers += response.items.length;
        response.items.forEach((item) => names.set(item.id, item.full_name));
        currentPage += 1;

        if (response.items.length === 0) break;
      } while (
        loadedUsers < totalUsers &&
        creatorIds.some((id) => !names.has(id))
      );

      return names;
    },
  });
}

export function useCheckoutMutation() {
  return useMutation({
    mutationFn: (request: CheckoutSessionRequest) =>
      api.payment.raw
        .post("api/v1/subscriptions/checkout-session", { json: request })
        .json<CheckoutSessionResponse>(),
  });
}

export function useEnterpriseInquiryMutation() {
  return useMutation({
    mutationFn: (request: EnterpriseInquiryRequest) =>
      api.payment.raw
        .post("api/v1/enterprise-inquiries", { json: request })
        .json<EnterpriseInquiryResponse>(),
  });
}

export function usePortalMutation() {
  return useMutation({
    mutationFn: (returnUrl: string) =>
      api.payment.raw
        .post("api/v1/subscriptions/portal-session", {
          json: { return_url: returnUrl },
        })
        .json<PortalSessionResponse>(),
  });
}

export function useIntervalPreviewMutation() {
  return useMutation({
    mutationFn: (interval: BillingInterval) =>
      api.payment.raw
        .post("api/v1/subscriptions/interval/preview", {
          json: { interval },
        })
        .json<IntervalPreviewResponse>(),
  });
}

export function useIntervalChangeMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (interval: BillingInterval) =>
      api.payment.raw
        .post("api/v1/subscriptions/interval", { json: { interval } })
        .json<IntervalChangeResponse>(),
    onSuccess: async (response) => {
      queryClient.setQueryData(billingQueryKeys.subscription, response);
      await invalidateBillingUsage(queryClient);
    },
    onError: async (error) => {
      if (error instanceof HTTPError && error.response.status === 409) {
        await queryClient.invalidateQueries({
          queryKey: billingQueryKeys.subscription,
        });
      }
    },
  });
}
